import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import { useGetBusinessListNamesQuery } from "lib/features/businessList/businessListService";
import { BusinessList } from "lib/features/businessList/types";
import {
  useCreateQRMutation,
  useUpdateQRMutation,
} from "lib/features/qrList/qrListService";
import { QRList, QRListDTO } from "lib/features/qrList/types";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createBusinessListStyles } from "styles/BusinessListDirectory/createBusinessList.styles";
import * as Yup from "yup";
import Label from "../NewItemForm/components/Label";

interface CreateQRFormProps {
  userId: number;
  qrcode: QRList | undefined;
  isEdit: boolean;
}

const validationSchema = Yup.object().shape({
  qr_name: Yup.string().required("QR name is required"),
});

const CreateQRForm = ({ userId, qrcode, isEdit }: CreateQRFormProps) => {
  const navigate = useNavigate();

  const [formValues, setFormValues] = useState<QRListDTO>({
    qr_name: qrcode?.qr_name ? qrcode?.qr_name : "",
    business_list_id: qrcode?.business_list?.id
      ? qrcode?.business_list?.id
      : undefined,
    user_id: userId,
  });

  const { data: businessListData } = useGetBusinessListNamesQuery(
    { id: userId },
    { skip: !userId }
  );

  const [createQR, { isLoading: createLoading }] = useCreateQRMutation();

  const [updateQR, { isLoading: updateLoading }] = useUpdateQRMutation();

  const onSubmit = async (values: QRListDTO) => {
    try {
      if (!isEdit) {
        await createQR(values).unwrap();
      } else if (isEdit && qrcode?.id) {
        await updateQR({ id: qrcode?.id, body: values }).unwrap();
      } else {
        await createQR(values).unwrap();
      }

      navigate(`/qrlist/${userId}`);
    } catch (error) {
      console.log("Error creating QR Code:", error);
    }
  };

  return (
    <Formik
      initialValues={formValues}
      validationSchema={validationSchema}
      onSubmit={(values) => onSubmit(values)}
    >
      {({ errors, touched, setFieldValue }) => {
        const handleChange = (e: React.ChangeEvent<any>) => {
          const { name, value } = e.target;
          setFieldValue(name, value);
          setFormValues((prevValues: any) => ({
            ...prevValues,
            [name]: value,
          }));
        };

        return (
          <Form>
            {/* QR Name */}
            <Box>
              <Label label="QR Code Name" required={true} />
              <Field
                as={TextField}
                name="qr_name"
                fullWidth
                onChange={handleChange}
                error={touched.qr_name && Boolean(errors.qr_name)}
              />
              {errors.qr_name && touched.qr_name && (
                <Typography color="red" fontSize="12px">
                  {errors.qr_name}
                </Typography>
              )}
            </Box>

            {/* Add List */}
            <Box mt={2}>
              <Label label="Add List" required={true} />
              <FormControl fullWidth>
                <Field
                  as={Select}
                  name="business_list_id"
                  onChange={handleChange}
                  value={formValues.business_list_id || ""}
                  displayEmpty
                  sx={{
                    "& .MuiSelect-root": {
                      paddingTop: "12px",
                      paddingBottom: "12px",
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select Existing List
                  </MenuItem>

                  {businessListData?.map((list: BusinessList) => (
                    <MenuItem key={list.id} value={list.id}>
                      {list.list_name}
                    </MenuItem>
                  ))}
                </Field>
                {errors.business_list_id && touched.business_list_id && (
                  <Typography color="red" fontSize="12px">
                    {errors.business_list_id}
                  </Typography>
                )}
              </FormControl>
            </Box>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              sx={createBusinessListStyles.actionBtn}
              disabled={createLoading || updateLoading}
            >
              Save
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CreateQRForm;
